import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Observable, Subscription, of, combineLatest, concat } from 'rxjs';
import { map, tap, takeWhile, filter } from 'rxjs/operators';
import { Store, Select } from '@ngxs/store';
import { ToastrService, ToastContainerDirective } from 'ngx-toastr';
import { MenuState } from '@nimbus-library/features/menu';
import { TranslationComponentObserverService } from '@nimbus-library/features/localization/services/translation-component-observer.service';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { TrackingService } from '@nimbus-library/features/tracking/tracking.service';
import { IMicrosoftTeamsContextAccessor } from '@nimbus-library/features/microsoft-teams/contract/i-microsoft-teams-context.accessor';
import { ITeamsContext } from '@nimbus-library/features/microsoft-teams/models/i-teams.context';
import { AppUpdateService } from '@nimbus-global/features/shared/services/app-update.service';
import { CompanionSplitScreenService } from '@nimbus-library/features/companion-chat/services/companion-split-screen.service';
import { InitializationService } from './features/initialization/initialization.service';
import { TeamState } from './common/store/teams/teams.state';

@Component({
  standalone: false,
  selector: 'nimbus-app-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TranslationComponentObserverService],
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild(ToastContainerDirective, { static: true }) public toastContainer: ToastContainerDirective;

  @Select(TeamState.loading)
  public loading$: Observable<boolean>;
  @Select(TeamState.loaded)
  public loadedState$: Observable<boolean>;

  private readonly subscription$: Subscription;

  public title = 'Nimbus';
  public showLoadingScreen$: Observable<boolean>;
  public context$: Observable<ITeamsContext>;
  public loaded$: Observable<boolean>;
  public showMenu$: Observable<boolean>;
  public isStandAlone: boolean;
  public isAuthenticated$: Observable<boolean>;
  public companionChatActive$: Observable<boolean>;

  public constructor(
    private readonly store: Store,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly router: Router,
    public readonly applicationElement: ElementRef,
    private readonly authenticationService: IAuthenticationService,
    private readonly initializationService: InitializationService,
    private readonly teamsService: IMicrosoftTeamsContextAccessor,
    private readonly toastrService: ToastrService,
    private readonly trackingService: TrackingService,
    private readonly companionSplitScreenService: CompanionSplitScreenService,
    appUpdateService: AppUpdateService,
  ) {
    this.subscription$ = new Subscription();
    appUpdateService.initialize();
  }

  public ngOnInit(): void {
    // TODO: Remove this when the provisioning issue has been investigated
    console.log('App started with', window.location.href);
    this.subscription$.add(
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd || event instanceof NavigationStart)).subscribe((ev) => {
        if (ev instanceof NavigationEnd) {
          console.log('NavigationEnd', ev.url, ev.urlAfterRedirects, window.location.href);
        } else if (ev instanceof NavigationStart) {
          console.log('NavigationStart', ev.url, ev.navigationTrigger, window.location.href);
        }
      }),
    );
    this.toastrService.overlayContainer = this.toastContainer;
    this.context$ = this.initializationService.context$;
    this.showMenu$ = this.store.select(MenuState.show);
    this.companionChatActive$ = this.companionSplitScreenService.active$;

    this.subscription$.add(
      combineLatest([this.initializationService.routeData$, concat(of(false), this.loadedState$)])
        .pipe(
          map(([options, loaded]) => ({ loadContext: options.loadContext, loaded })),
          tap((options) => {
            this.loaded$ = options.loadContext ? this.loadedState$ : of(true);
            this.changeDetector.detectChanges();
          }),
          takeWhile((options) => !options.loaded),
        )
        .subscribe(),
    );

    this.isStandAlone = this.teamsService.isStandAlone();
    this.isAuthenticated$ = this.authenticationService.isAuthenticated$;
    this.initializationService.initialize().then((subscription$) => this.subscription$.add(subscription$));
    this.trackingService.initialize();
  }

  public ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }
}
